// ============================================================================
// ENGAGEMENT TYPES (Aufträge)
// Comprehensive type definitions for the Engagements feature
// ============================================================================

// ============================================================================
// AUFTRAGSTYPEN (Engagement Types)
// Jeder Typ hat unterschiedliche Eigenschaften und Anforderungen
// ============================================================================

export type EngagementType = 
  | 'project'          // Klassisches Projekt mit Milestones & Deliverables
  | 'managed_service'  // Laufender Service ohne festes Ende
  | 'body_leasing'     // FTE-basiert, Mitarbeiter beim Kunden
  | 'prestudy'         // Vorstudie, kurze Analyse
  | 'workshop'         // 1-5 Tage, intensiv
  | 'hackathon'        // 1-3 Tage, Innovation
  | 'poc'              // Proof of Concept, 2-4 Wochen
  | 'pilot'            // Pilot-Phase, 1-3 Monate, kann zu Projekt werden
  | 'audit'            // Einmalige Prüfung mit Report
  | 'maintenance';     // Wartungsauftrag, wiederkehrende Tasks

// Komplexitätsstufe bestimmt UI und Pflichtfelder
export type EngagementComplexity = 'simple' | 'standard' | 'complex';

// Konfiguration pro Auftragstyp
export interface EngagementTypeConfig {
  label: { de: string; en: string };
  description: { de: string; en: string };
  complexity: EngagementComplexity;
  hasEndDate: boolean;          // Hat festes Ende?
  hasMilestones: boolean;       // Milestones relevant?
  hasRecurringTasks: boolean;   // Wiederkehrende Aufgaben?
  hasSLA: boolean;              // SLA-Tracking?
  canConvertToProject: boolean; // Kann in Projekt überführt werden?
  typicalDuration?: { de: string; en: string };     // Typische Dauer
  icon: string;                 // Lucide Icon Name
}

export const ENGAGEMENT_TYPE_CONFIG: Record<EngagementType, EngagementTypeConfig> = {
  project: {
    label: { de: 'Projekt', en: 'Project' },
    description: { de: 'Klassisches Projekt mit klarem Ziel und Lieferergebnissen', en: 'Classic project with clear goals and deliverables' },
    complexity: 'complex',
    hasEndDate: true,
    hasMilestones: true,
    hasRecurringTasks: false,
    hasSLA: false,
    canConvertToProject: false,
    typicalDuration: { de: '3-12 Monate', en: '3-12 months' },
    icon: 'FolderKanban'
  },
  managed_service: {
    label: { de: 'Managed Service', en: 'Managed Service' },
    description: { de: 'Laufender Service mit SLA-Vereinbarungen', en: 'Ongoing service with SLA agreements' },
    complexity: 'standard',
    hasEndDate: false,
    hasMilestones: false,
    hasRecurringTasks: true,
    hasSLA: true,
    canConvertToProject: false,
    icon: 'Server'
  },
  body_leasing: {
    label: { de: 'Body Leasing', en: 'Body Leasing' },
    description: { de: 'Mitarbeiter-Überlassung beim Kunden', en: 'Staff augmentation at customer site' },
    complexity: 'simple',
    hasEndDate: false,
    hasMilestones: false,
    hasRecurringTasks: false,
    hasSLA: false,
    canConvertToProject: false,
    icon: 'UserCheck'
  },
  prestudy: {
    label: { de: 'Vorstudie', en: 'Pre-Study' },
    description: { de: 'Kurze Analyse vor größerem Engagement', en: 'Short analysis before larger engagement' },
    complexity: 'simple',
    hasEndDate: true,
    hasMilestones: false,
    hasRecurringTasks: false,
    hasSLA: false,
    canConvertToProject: true,
    typicalDuration: { de: '1-4 Wochen', en: '1-4 weeks' },
    icon: 'Search'
  },
  workshop: {
    label: { de: 'Workshop', en: 'Workshop' },
    description: { de: 'Intensiver Workshop mit definiertem Ergebnis', en: 'Intensive workshop with defined outcome' },
    complexity: 'simple',
    hasEndDate: true,
    hasMilestones: false,
    hasRecurringTasks: false,
    hasSLA: false,
    canConvertToProject: true,
    typicalDuration: { de: '1-5 Tage', en: '1-5 days' },
    icon: 'Users'
  },
  hackathon: {
    label: { de: 'Hackathon', en: 'Hackathon' },
    description: { de: 'Intensive Innovations-Session', en: 'Intensive innovation session' },
    complexity: 'simple',
    hasEndDate: true,
    hasMilestones: false,
    hasRecurringTasks: false,
    hasSLA: false,
    canConvertToProject: true,
    typicalDuration: { de: '1-3 Tage', en: '1-3 days' },
    icon: 'Zap'
  },
  poc: {
    label: { de: 'Proof of Concept', en: 'Proof of Concept' },
    description: { de: 'Machbarkeitsnachweis für eine Idee oder Technologie', en: 'Feasibility proof for an idea or technology' },
    complexity: 'standard',
    hasEndDate: true,
    hasMilestones: true,
    hasRecurringTasks: false,
    hasSLA: false,
    canConvertToProject: true,
    typicalDuration: { de: '2-4 Wochen', en: '2-4 weeks' },
    icon: 'FlaskConical'
  },
  pilot: {
    label: { de: 'Pilot', en: 'Pilot' },
    description: { de: 'Test-Phase vor Vollprojekt', en: 'Test phase before full project' },
    complexity: 'standard',
    hasEndDate: true,
    hasMilestones: true,
    hasRecurringTasks: false,
    hasSLA: false,
    canConvertToProject: true,
    typicalDuration: { de: '1-3 Monate', en: '1-3 months' },
    icon: 'Rocket'
  },
  audit: {
    label: { de: 'Audit', en: 'Audit' },
    description: { de: 'Einmalige Prüfung mit Abschlussbericht', en: 'One-time review with final report' },
    complexity: 'simple',
    hasEndDate: true,
    hasMilestones: false,
    hasRecurringTasks: false,
    hasSLA: false,
    canConvertToProject: false,
    typicalDuration: { de: '1-2 Wochen', en: '1-2 weeks' },
    icon: 'ClipboardCheck'
  },
  maintenance: {
    label: { de: 'Wartung', en: 'Maintenance' },
    description: { de: 'Laufende Wartungsarbeiten mit wiederkehrenden Tasks', en: 'Ongoing maintenance with recurring tasks' },
    complexity: 'simple',
    hasEndDate: false,
    hasMilestones: false,
    hasRecurringTasks: true,
    hasSLA: true,
    canConvertToProject: false,
    icon: 'Wrench'
  }
};

// ============================================================================
// STATUS & BILLING
// ============================================================================

export type EngagementStatus = 'draft' | 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';
export type BillingType = 'fixed_price' | 'time_and_material' | 'retainer' | 'fte_based';

export const ENGAGEMENT_STATUS_CONFIG: Record<EngagementStatus, { label: { de: string; en: string }; color: string }> = {
  draft: { label: { de: 'Entwurf', en: 'Draft' }, color: 'gray' },
  planning: { label: { de: 'Planung', en: 'Planning' }, color: 'blue' },
  active: { label: { de: 'Aktiv', en: 'Active' }, color: 'green' },
  on_hold: { label: { de: 'Pausiert', en: 'On Hold' }, color: 'yellow' },
  completed: { label: { de: 'Abgeschlossen', en: 'Completed' }, color: 'purple' },
  cancelled: { label: { de: 'Abgebrochen', en: 'Cancelled' }, color: 'red' }
};

export const BILLING_TYPE_CONFIG: Record<BillingType, { label: { de: string; en: string } }> = {
  fixed_price: { label: { de: 'Festpreis', en: 'Fixed Price' } },
  time_and_material: { label: { de: 'Zeit & Material', en: 'Time & Material' } },
  retainer: { label: { de: 'Retainer', en: 'Retainer' } },
  fte_based: { label: { de: 'FTE-basiert', en: 'FTE-based' } }
};

// ============================================================================
// LEADHUB SNAPSHOT TYPES (denormalisierte Daten für schnellen Zugriff)
// ============================================================================

export interface CustomerCompanySnapshot {
  id: string;
  name: string;
  industry?: string;
  domain?: string;
  logoUrl?: string;
  isCustomer: boolean;
  customerSince?: Date;
}

export interface OpportunitySnapshot {
  id: string;
  title: string;
  value?: number;
  stage: string;
  expectedCloseDate?: Date;
  actualCloseDate?: Date;
}

export interface OfferSnapshot {
  id: string;
  offerNumber: string;
  title: string;
  total: number;
  currency: 'EUR' | 'USD' | 'GBP';
  status: string;
  validUntil?: Date;
}

export interface ContactSnapshot {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  position?: string;
  isKeyDecisionMaker?: boolean;
}

export interface ProductSnapshot {
  id: string;
  name: string;
  category?: string;
  price?: number;
}

// ============================================================================
// TEAM ALLOCATION & UTILIZATION
// ============================================================================

export type TeamRole = 'lead' | 'member' | 'consultant' | 'support';
export type LocationType = 'onsite' | 'remote' | 'hybrid';

// Team-Zuordnung mit Auslastung
export interface TeamAllocation {
  userId: string;
  userName: string;
  avatarUrl?: string;
  role: TeamRole;
  allocationPercentage: number;        // 0-100, z.B. 50% = halbe Kraft
  startDate: Date;
  endDate?: Date;
  skills?: string[];                   // Relevante Skills für diesen Auftrag
}

// FTE-Zuordnung für Body Leasing
export interface FTEAllocation {
  userId: string;
  userName: string;
  avatarUrl?: string;
  ftePercentage: number;               // 100 = 1 FTE, 50 = 0.5 FTE
  dailyRate?: number;                  // Tagessatz
  location: LocationType;
  customerContact?: string;            // Ansprechpartner beim Kunden
  startDate: Date;
  endDate?: Date;
}

// Utilization-Berechnung
export interface TeamMemberUtilization {
  userId: string;
  userName: string;
  avatarUrl?: string;
  totalAllocation: number;             // Gesamt-Auslastung in %
  engagements: {
    engagementId: string;
    engagementName: string;
    engagementType: EngagementType;
    allocation: number;
  }[];
  availableCapacity: number;           // Verfügbare Kapazität in %
  isOverallocated: boolean;            // > 100%
}

// Matrix-Struktur für Anzeige
export interface UtilizationMatrix {
  members: {
    userId: string;
    userName: string;
    avatarUrl?: string;
  }[];
  engagements: {
    engagementId: string;
    engagementName: string;
    engagementType: EngagementType;
    customerId?: string;
    customerName?: string;
  }[];
  allocations: {
    memberId: string;
    engagementId: string;
    percentage: number;
    role: TeamRole;
  }[];
}

// ============================================================================
// RECURRING TASKS (für Managed Services & Wartung)
// ============================================================================

export type RecurringFrequency = 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly';

export interface RecurringTaskTemplate {
  id: string;
  title: string;
  description?: string;
  frequency: RecurringFrequency;
  dayOfWeek?: number;                  // 0-6 für weekly
  dayOfMonth?: number;                 // 1-31 für monthly
  estimatedHours?: number;
  assignedToId?: string;
  assignedToName?: string;
  priority: 'low' | 'medium' | 'high';
  lastGeneratedAt?: Date;
  nextDueDate?: Date;
}

// ============================================================================
// MILESTONES
// ============================================================================

export type MilestoneStatus = 'pending' | 'in_progress' | 'completed' | 'delayed';

export interface EngagementMilestone {
  id: string;
  name: string;
  description?: string;
  dueDate: Date;
  status: MilestoneStatus;
  deliverables?: string[];
  completedAt?: Date;
  order: number;
}

// ============================================================================
// STAKEHOLDERS
// ============================================================================

export type StakeholderRole = 'sponsor' | 'owner' | 'contributor' | 'observer';

export interface EngagementStakeholder {
  id: string;
  userId?: string;              // Interner User (aus TeamMemberProfile)
  contactId?: string;           // Externer Kontakt (aus Leads)
  name: string;
  role: StakeholderRole;
  email?: string;
  isInternal: boolean;
}

// ============================================================================
// BUDGET
// ============================================================================

export interface EngagementBudget {
  totalBudget: number;
  currency: 'EUR' | 'USD' | 'GBP';
  plannedHours?: number;
  hourlyRate?: number;
  dailyRate?: number;           // Für Body Leasing
  monthlyRetainer?: number;     // Für Managed Services
  allocatedExpenses?: number;
  actualExpenses?: number;
  actualHours?: number;
  budgetUsedPercentage?: number;
}

// ============================================================================
// DOCUMENTS
// ============================================================================

export type DocumentType = 'contract' | 'offer' | 'specification' | 'requirement' | 'report' | 'sla' | 'other';

export interface EngagementDocument {
  id: string;
  fileName: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
  fileType: DocumentType;
  uploadedAt: Date;
  uploadedBy: string;
  linkedOfferId?: string;
  extractedData?: EngagementExtractedData;
}

export interface EngagementExtractedData {
  extractedName?: string;
  extractedBudget?: number;
  extractedStartDate?: string;
  extractedEndDate?: string;
  extractedEngagementType?: EngagementType;
  extractedMilestones?: string[];
  extractedDeliverables?: string[];
  extractedSLAs?: string[];
  confidence: 'high' | 'medium' | 'low';
}

// ============================================================================
// SLA TRACKING (für Managed Services)
// ============================================================================

export type SLAMetric = 'uptime' | 'response_time' | 'resolution_time' | 'custom';
export type SLAUnit = 'percent' | 'hours' | 'minutes' | 'custom';
export type SLAPeriod = 'daily' | 'weekly' | 'monthly' | 'quarterly';
export type SLAStatus = 'met' | 'violated' | 'at_risk' | 'pending';

export interface SLADefinition {
  id: string;
  name: string;                        // z.B. "Verfügbarkeit", "Reaktionszeit"
  description?: string;
  metric: SLAMetric;
  target: number;                      // z.B. 99.9 für 99.9% Uptime
  unit: SLAUnit;
  measurementPeriod: SLAPeriod;
  penaltyPerViolation?: number;        // Vertragsstrafe pro Verletzung
  warningThreshold?: number;           // Warnung bei z.B. 99.5%
}

export interface SLATrackingRecord {
  id: string;
  slaDefinitionId: string;
  slaName: string;
  period: string;                      // z.B. "2026-02" für Februar
  targetValue: number;
  actualValue: number;
  status: SLAStatus;
  notes?: string;
  recordedAt: Date;
  recordedBy?: string;
}

// ============================================================================
// CONTRACT RENEWAL (Vertragsverlängerung)
// ============================================================================

export type RenewalStatus = 'pending' | 'in_negotiation' | 'renewed' | 'cancelled' | 'not_applicable';

export interface ContractRenewal {
  reminderDate?: Date;               // Erinnerung X Monate vor Vertragsende
  renewalStatus: RenewalStatus;
  renewedUntil?: Date;               // Neues Enddatum nach Verlängerung
  renewalHistory?: RenewalHistoryEntry[];
}

export interface RenewalHistoryEntry {
  id: string;
  renewedAt: Date;
  previousEndDate: Date;
  newEndDate: Date;
  notes?: string;
  renewedBy: string;
}

// ============================================================================
// TEMPLATES (Vorlagen)
// ============================================================================

export interface EngagementTemplate {
  id: string;
  orgId: string;
  name: string;                        // z.B. "Standard Website-Projekt"
  description?: string;
  engagementType: EngagementType;
  isDefault: boolean;                  // Standard-Vorlage für diesen Typ?
  
  // Vordefinierte Werte
  defaultBillingType?: BillingType;
  defaultMilestones?: Omit<EngagementMilestone, 'id' | 'status' | 'completedAt'>[];
  defaultRecurringTasks?: Omit<RecurringTaskTemplate, 'id' | 'lastGeneratedAt' | 'nextDueDate'>[];
  defaultSLAs?: Omit<SLADefinition, 'id'>[];
  suggestedDuration?: number;          // in Tagen
  suggestedBudgetRange?: { min: number; max: number };
  suggestedTeamSize?: { min: number; max: number };
  
  // Metadata
  usageCount: number;                  // Wie oft verwendet?
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// CAPACITY FORECAST (Kapazitäts-Prognose)
// ============================================================================

export interface CapacityForecastPeriod {
  period: string;                      // z.B. "2026-03" für März
  allocatedCapacity: number;           // Gebuchte Kapazität in %
  availableCapacity: number;           // Verfügbare Kapazität in %
  engagements: {
    engagementId: string;
    engagementName: string;
    allocation: number;
  }[];
}

export interface CapacityForecast {
  userId: string;
  userName: string;
  avatarUrl?: string;
  periods: CapacityForecastPeriod[];
}

export interface TeamCapacityForecast {
  periods: {
    period: string;
    totalTeamCapacity: number;         // In FTE
    allocatedCapacity: number;         // In FTE
    availableCapacity: number;         // In FTE
    utilizationPercentage: number;
  }[];
  memberForecasts: CapacityForecast[];
}

// ============================================================================
// MAIN ENGAGEMENT INTERFACE
// ============================================================================

export interface Engagement {
  id: string;
  orgId: string;
  
  // ============================================================================
  // BASIS-INFORMATIONEN
  // ============================================================================
  name: string;
  description?: string;
  engagementType: EngagementType;      // Art des Auftrags
  billingType: BillingType;            // Abrechnungsmodell
  status: EngagementStatus;
  complexity: EngagementComplexity;    // Automatisch aus Type, kann überschrieben werden
  
  // ============================================================================
  // ZEITRAHMEN (je nach Auftragstyp)
  // ============================================================================
  startDate: Date;
  endDate?: Date;                      // Optional bei laufenden Aufträgen
  isOpenEnded: boolean;                // true = kein festes Ende (Managed Service, etc.)
  contractEndDate?: Date;              // Vertragsende bei laufenden Aufträgen
  
  // Für komplexe Aufträge
  milestones?: EngagementMilestone[];
  
  // ============================================================================
  // VERTRAGSVERLÄNGERUNG (für Managed Services, Body Leasing, Wartung)
  // ============================================================================
  contractRenewal?: ContractRenewal;
  
  // ============================================================================
  // SLA-TRACKING (für Managed Services)
  // ============================================================================
  slaDefinitions?: SLADefinition[];
  
  // ============================================================================
  // LEADHUB VERKNÜPFUNGEN (ALLE OPTIONAL!)
  // ============================================================================
  isInternal: boolean;
  customerId?: string;
  customer?: CustomerCompanySnapshot;
  customerNameManual?: string;
  
  opportunityId?: string;
  opportunity?: OpportunitySnapshot;
  
  offerId?: string;
  offer?: OfferSnapshot;
  
  contactIds?: string[];
  contacts?: ContactSnapshot[];
  
  productIds?: string[];
  products?: ProductSnapshot[];
  
  // ============================================================================
  // TEAM & RESSOURCEN
  // ============================================================================
  managerId: string;                   // Verantwortlicher
  managerName: string;
  teamAllocations: TeamAllocation[];   // Team mit Auslastung
  stakeholders?: EngagementStakeholder[];
  
  // Für Body Leasing: FTE-Zuordnung
  fteAllocations?: FTEAllocation[];
  
  // ============================================================================
  // BUDGET & FINANZEN
  // ============================================================================
  budget?: EngagementBudget;
  
  // ============================================================================
  // AUFGABEN
  // ============================================================================
  taskIds: string[];
  
  // Für wiederkehrende Aufträge (Managed Service, Wartung)
  recurringTasks?: RecurringTaskTemplate[];
  
  // ============================================================================
  // DOKUMENTE & ZIELE
  // ============================================================================
  documents: EngagementDocument[];
  objectives?: string[];
  deliverables?: string[];
  
  // ============================================================================
  // KONVERTIERUNG
  // ============================================================================
  // Wenn POC/Pilot zu Projekt wird
  convertedFromId?: string;            // Ursprungs-Auftrag
  convertedFromType?: EngagementType;
  convertedToId?: string;              // Folge-Projekt
  
  // ============================================================================
  // TEMPLATE
  // ============================================================================
  createdFromTemplateId?: string;
  
  // ============================================================================
  // PROGRESS TRACKING
  // ============================================================================
  progressPercentage?: number;         // 0-100
  
  // ============================================================================
  // METADATA
  // ============================================================================
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

// ============================================================================
// STATISTICS
// ============================================================================

export interface EngagementStatistics {
  totalEngagements: number;
  activeEngagements: number;
  byType: Partial<Record<EngagementType, number>>;
  byStatus: Partial<Record<EngagementStatus, number>>;
  totalBudget: number;
  usedBudget: number;
  teamUtilization: {
    averageUtilization: number;
    overallocatedMembers: number;
    underutilizedMembers: number;
  };
  upcomingDeadlines: number;
  overdueMilestones: number;
  expiringContracts: number;
}

// ============================================================================
// AI TYPES
// ============================================================================

export interface EngagementTypeDetection {
  suggestedType: EngagementType;
  confidence: number;
  reasoning: string;
  alternativeTypes?: { type: EngagementType; confidence: number }[];
}

export interface EffortEstimate {
  estimatedHours: { min: number; expected: number; max: number };
  estimatedBudget: { min: number; expected: number; max: number };
  estimatedDuration: { min: number; expected: number; max: number }; // in days
  confidence: 'low' | 'medium' | 'high';
  basedOnSimilarEngagements: number;
  comparableEngagements: {
    id: string;
    name: string;
    actualHours: number;
    actualBudget: number;
    duration: number;
  }[];
}

export interface TeamSuggestion {
  userId: string;
  userName: string;
  avatarUrl?: string;
  matchScore: number;           // 0-100, wie gut passt Person?
  availableCapacity: number;    // Verfügbare Kapazität im Zeitraum
  relevantSkills: string[];
  currentEngagements: string[]; // Namen aktueller Aufträge
  recommendation: string;       // "Ideal für Lead-Rolle" etc.
  suggestedRole: TeamRole;
  suggestedAllocation: number;
}

export interface EngagementRisk {
  type: 'budget' | 'deadline' | 'scope' | 'resource' | 'quality' | 'sla';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  recommendation: string;
  affectedMilestones?: string[];
}

export interface MilestoneSuggestion {
  name: string;
  description: string;
  suggestedDuration: number; // in days
  deliverables: string[];
  order: number;
}

export interface TaskSuggestion {
  title: string;
  description: string;
  estimatedHours: number;
  priority: 'low' | 'medium' | 'high';
  suggestedAssigneeSkills?: string[];
  milestoneId?: string;
}

// ============================================================================
// FILTER & QUERY TYPES
// ============================================================================

export interface EngagementFilters {
  engagementTypes?: EngagementType[];
  statuses?: EngagementStatus[];
  customerId?: string;
  managerId?: string;
  teamMemberId?: string;
  isInternal?: boolean;
  dateRange?: {
    start: Date;
    end: Date;
  };
  searchQuery?: string;
}

export interface EngagementSortOptions {
  field: 'name' | 'startDate' | 'endDate' | 'createdAt' | 'updatedAt' | 'status' | 'progressPercentage';
  direction: 'asc' | 'desc';
}

// ============================================================================
// FORM TYPES
// ============================================================================

export interface EngagementFormData {
  name: string;
  description?: string;
  engagementType: EngagementType;
  billingType: BillingType;
  complexity?: EngagementComplexity;
  startDate: Date;
  endDate?: Date;
  isOpenEnded: boolean;
  contractEndDate?: Date;
  isInternal: boolean;
  customerId?: string;
  customerNameManual?: string;
  opportunityId?: string;
  offerId?: string;
  contactIds?: string[];
  productIds?: string[];
  managerId: string;
  teamAllocations: TeamAllocation[];
  fteAllocations?: FTEAllocation[];
  budget?: Partial<EngagementBudget>;
  objectives?: string[];
  deliverables?: string[];
  milestones?: Omit<EngagementMilestone, 'id'>[];
  recurringTasks?: Omit<RecurringTaskTemplate, 'id'>[];
  slaDefinitions?: Omit<SLADefinition, 'id'>[];
  templateId?: string;
}

export interface QuickCreateFormData {
  name: string;
  engagementType: EngagementType;
  customerId?: string;
  customerNameManual?: string;
  startDate: Date;
  managerId: string;
}
